/* eslint-disable react/prop-types */
import React from 'react'
import { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import EditLyrics from '../../../Components/DashboardComponents/Lyrics/EditLyrics'
import ArtistAndSongName from '../../../Components/GeneralComponents/ArtistAndSongName/ArtistAndSongName'
import Loader from '../../../Components/GeneralComponents/Loader'
import { randomString } from '../../../Helpers/String+Extension'
import { useAuth } from '../../../Contexts/AuthContext'
import errorToast from '../../../Components/Toasts/ErrorToast'
import successToast from '../../../Components/Toasts/SuccessToast'
import TeaseVideo from './TeaseVideo'
import useKeyboardEvents from '../hooks/useKeyboardEvents'
import { useFetchTease } from '../hooks/useFetchTease'
import deepEqual from 'deep-equal'
import diff from 'deep-diff'
import usePopup from '../hooks/usePopup'
import { Wrapper } from '../../../Components/page/Wrapper'
import { Header } from '../../../Components/page/Header'
import { Navigation } from '../../../Components/page/Navigation'
import { HeaderInfo } from '../../../Components/page/HeaderInfo'
import PublicationBadge from '../../../Components/GeneralComponents/PublicationBadge'
import CheckboxButton from '../../../Components/GeneralComponents/CheckboxButton'
import { ButtonOutline } from '../../../Components/GeneralComponents/ButtonOutline/ButtonOutline'
import { renderBreadcrumbs } from './breadcrumbs'
import { Actions } from '../../../Components/page/Actions'
import OffsetActions from './OffsetActions'
import TextFields from './TextFields'
import classes from './index.module.css'
import { useSaveTeaseEdits } from '../hooks/useSaveTeaseEdits'
import { useQueryClient } from '@tanstack/react-query'
import { useCreateTease } from '../hooks/useCreateTease'
import { useEditTeaseLyrics } from '../hooks/useEditTeaseLyrics'
import CreateTeasePopupContent from './CreateTeasePopupContent'
import TeasePopup from '../TeasePopup'
import { useCreateTeaseVideo } from '../hooks/useCreateTeaseVideo'
import { useUploadTeaseVideo } from '../hooks/useUploadTeaseVideo'
import { isValidUrl } from '../helpers/isValidUrl'
import useDeviceAccessControl from '../../../Helpers/hooks/useDeviceAccessControl'
import DevicesRestrictionsNotification from '../../../Components/common/DevicesRestrictionsNotification'
import DisabledOverlay from '../../../Components/GeneralComponents/DisabledOverlay/DisabledOverlay'

export default function CreateTease() {
  const { getCurrentUser } = useAuth()
  const hasDeviceAccess = useDeviceAccessControl()

  const { id } = useParams()
  const videoElement = useRef()
  const initialTeaseRef = useRef()
  const navigate = useNavigate()
  const { openPopup, closePopup, isOpen } = usePopup()
  const queryClient = useQueryClient()
  const {
    mutate: saveTeaseEdits,
    isPending: isLoadingSaveTeaseEdits,
    isError: isErrorSaveTeaseEdits,
  } = useSaveTeaseEdits()
  const {
    mutate: createTease,
    isPending: isLoadingCreateTease,
    isError: isErrorCreateTease,
  } = useCreateTease()
  const {
    mutate: editTeaseLyrics,
    isPending: isLoadingEditTeaseLyrics,
    isError: isErrorEditTeaseLyrics,
  } = useEditTeaseLyrics()
  const {
    mutate: createTeaseVideo,
    isPending: isLoadingCreateTeaseVideo,
    isError: isErrorCreateTeaseVideo,
  } = useCreateTeaseVideo()
  const {
    mutate: uploadTeaseVideo,
    isPending: isLoadingUploadTeaseVideo,
    isError: isErrorUploadTeaseVideo,
  } = useUploadTeaseVideo()
  const [songData, setSongData] = useState(null)
  const [isLoopingLines, setIsLoopingLines] = useState(true)
  const [currentlyHighlightedLine, setCurrentlyHighlightedLine] = useState(null)
  const [currentlySelectedLine, setCurrentlySelectedLine] = useState(null)
  const [timer, setTimer] = useState(null)
  const [isTeaseChanged, setIsTeaseChanged] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [difference, setDifference] = useState(undefined)
  const [isDone, setIsDone] = useState(false)
  const [feelsSyncStatus, setFeelsSyncStatus] = useState('idle')

  const isSyncVerified = songData?.is_sync_verified
  const isTimingLocked = isSyncVerified || feelsSyncStatus === 'pending'
  const currentVideoOffset = Number(songData?.lyrics_adj) || 0
  const userEmail = getCurrentUser()?.email
  const songFID = songData?.f_id
  const isFeelsLocked = songData?.feels_locked || false
  const promotional_url = songData?.promoting?.url
  const promotional_url_display_text = songData?.promoting?.url_display_text
  const artist_f_id = songData?.artist.f_id
  const name = songData?.name
  const artist = songData?.artist
  const lyrics = songData?.lyrics_sync

  const saveTeaseSyncArgs = {
    songFID,
    video_offset: currentVideoOffset,
    lyrics_sync: lyrics,
    is_sync_verified: isSyncVerified,
    sync_user_id: userEmail,
    feels_locked: isFeelsLocked,
  }

  const promotionalTeaseData = {
    name,
    name_full: name,
    promotional_url: promotional_url || null,
    promotional_url_display_text: promotional_url_display_text || null,
    artist_f_id,
    songFID,
  }

  const {
    data: teaseData,
    isError: teaseError,
    isLoading: teaseIsLoading,
    isFetching: isTeaseFetching,
  } = useFetchTease({
    tease_id: id,
    refetchInterval: songData?.video?.video?.url ? false : 1000,
  })

  const isTeaseUpdating =
    isLoadingSaveTeaseEdits ||
    isLoadingCreateTease ||
    isLoadingEditTeaseLyrics ||
    isLoadingCreateTeaseVideo ||
    isLoadingUploadTeaseVideo ||
    isTeaseFetching

  const isTeaseUpdateError =
    isErrorSaveTeaseEdits ||
    isErrorCreateTease ||
    isErrorEditTeaseLyrics ||
    isErrorCreateTeaseVideo ||
    isErrorUploadTeaseVideo

  const setLyrics = lyrics_sync =>
    setSongData(prev => ({ ...prev, lyrics_sync }))

  const handleChanges = (...props) =>
    difference && [...new Set(difference)].some(item => props.includes(item))

  const onSyncVerifiedClick = () => {
    songData &&
      setSongData({ ...songData, is_sync_verified: !songData.is_sync_verified })
  }

  const handlePopupOpen = () => {
    openPopup()
    setIsEditing(true)
    videoElement.current.pause()
  }
  const handlePopupClose = () => {
    closePopup()
    setIsEditing(false)
    videoElement.current.play()
  }

  const onEditClick = () => {
    handlePopupOpen()
  }

  const onTextFieldValueChange = e => {
    setSongData({
      ...songData,
      promoting: { ...songData.promoting, [e.target.name]: e.target.value },
    })
  }

  const handleOptionSelect = option => {
    setSongData({
      ...songData,
      promoting: { ...songData.promoting, url_display_text: option },
    })
  }
  const onSaveClick = () => {
    if (songData?.promoting?.url && !isValidUrl(songData?.promoting?.url)) {
      errorToast(
        'The URL you entered is not valid. Please provide a valid URL starting with http:// or https://'
      )
      return
    }
    if (handleChanges('lyrics')) {
      handleEditTeaseLyrics({
        payload: {
          lyrics_sync: JSON.parse(
            JSON.stringify(prepareLyricsToSave(lyrics))
          ).map(lyric => {
            lyric.t_start = lyric.t_start - currentVideoOffset
            lyric.t_end = lyric.t_end - currentVideoOffset
            lyric.milliseconds = lyric.milliseconds - currentVideoOffset
            lyric.lrc_timestamp = tToTimestamp(lyric.t_start)
            return lyric
          }),
          edit_user_id: userEmail,
        },
        tease_id: songFID,
      })
    }

    if (
      !handleChanges('lyrics') &&
      handleChanges('lyrics_sync', 'lyrics_adj', 'is_sync_verified')
    ) {
      handleSyncTease(saveTeaseSyncArgs)
    }

    if (handleChanges('name', 'name_full', 'promoting')) {
      handleUpdateTease(promotionalTeaseData)
    }

    if (handleChanges('video')) {
      songData.video.file &&
        handleCreateTeaseVideo({
          payload: {
            video_type: songData.video.video.video_type,
            filesize: songData.video.video.filesize,
            dimensions: songData.video.video.dimensions,
            orientation: 'vertical',
          },
          tease_id: songData.f_id,
        })
    }
  }

  const onDoneClick = () => {
    setIsDone(true)
    onSaveClick()
  }

  useEffect(() => {
    if (!isTeaseUpdating && !isTeaseUpdateError && !isTeaseChanged && isDone) {
      queryClient.clear()
      navigate(`/tease/${songFID}`)
    }
  }, [isTeaseUpdating, isTeaseUpdateError, isTeaseChanged, isDone])

  const onLoopLinesClick = () => {
    setIsLoopingLines(!isLoopingLines)
  }
  const onVideoOffsetValueChange = e => {
    if (isTimingLocked) return
    let numericValue = e.target.value.replace(/\D/g, '')
    numericValue = numericValue.replace(/^0+(?=\d)/, '')

    setSongData({ ...songData, [e.target.name]: numericValue })
    setCurrentlyHighlightedLine(null)
  }

  const handleUploadTeaseVideo = videoData => {
    uploadTeaseVideo(videoData, {
      onSuccess: () => {
        successToast('Video uploaded successfully')

        queryClient.invalidateQueries(['tease', songFID])
      },
      onError: error => {
        errorToast('Error uploading video', error)
      },
    })
  }
  const handleCreateTeaseVideo = videoData => {
    createTeaseVideo(videoData, {
      onSuccess: video => {
        successToast('Video created successfully')
        handleUploadTeaseVideo({
          uploadURL: video.upload_url[0],
          file: songData.video.file,
        })
      },
      onError: error => {
        errorToast('Error creating video', error)
      },
    })
  }

  const handleSyncTease = lyricsData => {
    saveTeaseEdits(lyricsData, {
      onSuccess: async data => {
        successToast('Lyrics synchronized successfully')
        await queryClient.invalidateQueries(['tease', songFID])
      },
      onError: error => {
        errorToast('Error synchronizing lyrics', error)
      },
    })
  }

  const handleUpdateTease = teaseData => {
    createTease(teaseData, {
      onSuccess: () => {
        successToast('Tease updated successfully')

        queryClient.invalidateQueries(['tease', songFID])
      },
      onError: error => {
        errorToast('Error updating tease', error)
      },
    })
  }

  const handleEditTeaseLyrics = lyricsData => {
    editTeaseLyrics(lyricsData, {
      onSuccess: async () => {
        successToast('Lyrics updated successfully')
        await queryClient.invalidateQueries(['tease', songFID])
      },
      onError: error => {
        errorToast('Error updating lyrics', error)
      },
    })
  }

  const handleFeelsSyncComplete = (data) => {
    // Pause video to prevent play() interruption errors
    if (videoElement.current) {
      videoElement.current.pause()
    }
    
    setSongData(prevData => ({
      ...prevData,
      ...data,
      lyrics_sync: data.lyrics_sync.map(item => ({
        ...item,
        identifier: randomString(10)
      }))
    }))
    
    // Reset any selected line to avoid timing issues
    setCurrentlySelectedLine(null)
    setCurrentlyHighlightedLine(null)
    
    // Clear video offset since new sync data should have proper timing
    setSongData(prev => ({ ...prev, lyrics_adj: 0 }))
  }

  useEffect(() => {
    if (!!songData) {
      deepEqual(initialTeaseRef.current, songData)
        ? setIsTeaseChanged(false)
        : setIsTeaseChanged(true)
    }
  }, [songData])

  useEffect(() => {
    return () => {
      clearInterval(timer)
      setTimer(null)
    }
  }, [])

  useEffect(() => {
    if (teaseData) {
      const newTease = JSON.parse(JSON.stringify(teaseData))
      const lyricsSync = [...newTease.lyrics_sync].map(item => {
        item.identifier = randomString(10)
        return item
      })
      const newTeaseWithLyricsIdentifier = {
        ...newTease,
        lyrics_sync: lyricsSync,
      }

      if (
        handleChanges('lyrics') &&
        handleChanges('lyrics_sync', 'lyrics_adj', 'is_sync_verified')
      ) {
        const saveTeaseSyncArgs = {
          songFID,
          video_offset: currentVideoOffset,
          lyrics_sync: lyricsSync,
          is_sync_verified: isSyncVerified,
          sync_user_id: userEmail,
          feels_locked: isFeelsLocked,
        }
        handleSyncTease(saveTeaseSyncArgs)
      }

      setSongData(JSON.parse(JSON.stringify(newTeaseWithLyricsIdentifier)))
      initialTeaseRef.current = JSON.parse(
        JSON.stringify(newTeaseWithLyricsIdentifier)
      )
    }
  }, [teaseData])

  useEffect(() => {
    if (songData && initialTeaseRef.current) {
      const diffEdits = diff(songData, initialTeaseRef.current)?.reduce(
        (acc, item) => item.path && [...acc, item?.path[0]],
        []
      )
      setDifference(diffEdits)
    }
  }, [songData, initialTeaseRef.current])

  useEffect(() => {
    return () => {
      clearInterval(timer)
      setTimer(null)
    }
  }, [])

  //Hot keys setup

  const { isShiftDown, isAltDown, isXDown } = useKeyboardEvents({
    lyrics,
    currentlySelectedLine,
    handleLyricSelected,
    onSaveClick,
    videoElement,
    isTeaseChanged,
    isLoopingLines,
    setIsLoopingLines,
    handleSetCurrentVideoPlayerToLineAction,
    handleIncrementDecrementButton,
    handleIncrementDecrementToLineAndFollowingLines,
    isEditing,
    songData,
    isOpen,
    isTimingLocked,
  })

  useEffect(() => {
    if (currentlySelectedLine !== null) {
      setVideoPlayerAtLine(currentlySelectedLine)
    }
  }, [currentVideoOffset])

  useEffect(() => {
    clearInterval(timer)
    const newTimer = setInterval(() => {
      handleOnTimeUpdate()
    }, 5)
    setTimer(newTimer)

    return () => {
      clearInterval(timer)
      setTimer(null)
    }
  }, [isLoopingLines, lyrics, currentlySelectedLine])

  function prepareLyricsToSave(newLyrics) {
    var lyricsToSave = []
    for (var i = 0; i < newLyrics.length; i++) {
      if (newLyrics[i].line === '') {
        lyricsToSave.push({ line: '' })
      } else {
        const startTime =
          currentVideoOffset +
          newLyrics[i].t_start +
          (newLyrics[i].start_adj !== undefined ? newLyrics[i].start_adj : 0)
        const endTime =
          currentVideoOffset +
          newLyrics[i].t_end +
          (newLyrics[i].end_adj !== undefined ? newLyrics[i].end_adj : 0)

        lyricsToSave.push({
          line: newLyrics[i].line,
          t_end: endTime,
          t_start: startTime,
          duration: newLyrics[i].duration,
          milliseconds: startTime,
          lrc_timestamp: tToTimestamp(startTime),
        })
      }
    }
    return lyricsToSave
  }

  function zeroPad(num, places) {
    return String(num).padStart(places, '0')
  }

  function tToTimestamp(t) {
    if (!t) {
      return '[00:00.00]'
    }

    let s_raw = Math.floor(t / 1000.0)

    let m_ts = Math.floor(s_raw / 60)
    let s_ts = s_raw % 60
    let ss_ts = Math.round(t - s_raw * 1000) / 10.0

    return `[${zeroPad(m_ts, 2)}:${zeroPad(s_ts, 2)}.${zeroPad(ss_ts, 2)}]`
  }

  function handleLyricSelected(line) {
    if (
      currentlySelectedLine !== null &&
      currentlySelectedLine.identifier === line.identifier
    ) {
      setCurrentlySelectedLine(null)
    } else {
      setCurrentlySelectedLine(line)
      setVideoPlayerAtLine(line)
    }
  }

  function setVideoPlayerAtLine(line) {
    const currentStartTime =
      currentVideoOffset +
      line.t_start +
      (line.start_adj !== undefined ? line.start_adj : 0)
    videoElement.current.currentTime = currentStartTime / 1000
  }

  function setVideoPlayerAtEndOfALine(line) {
    const currentEndTime =
      currentVideoOffset +
      line.t_end +
      (line.end_adj !== undefined ? line.end_adj : 0) -
      1000
    videoElement.current.currentTime = currentEndTime / 1000
  }

  function handleOnTimeUpdate() {
    if (videoElement.current === undefined || videoElement.current === null) {
      return
    }
    const line = lyrics.find(item => {
      const currentStartTime =
        currentVideoOffset +
        item.t_start +
        (item.start_adj !== undefined ? item.start_adj : 0)
      const currentEndTime =
        currentVideoOffset +
        item.t_end +
        (item.end_adj !== undefined ? item.end_adj : 0)
      return (
        videoElement.current.currentTime * 1000 > currentStartTime &&
        videoElement.current.currentTime * 1000 < currentEndTime
      )
    })
    if (line !== undefined) {
      setCurrentlyHighlightedLine(line)
    } else {
      setCurrentlyHighlightedLine(null)
    }

    if (currentlySelectedLine !== null && isLoopingLines) {
      const currentStartTime = getCurrentSelectedLineStartTime()
      const currentEndTime = getCurrentSelectedLineEndTime()
      // This is very handy for debugging of the line looping
      // console.log("START: " + currentStartTime)
      // console.log("END: " + currentEndTime)
      // console.log("CURRENT: " + Math.round((videoElement.current.currentTime + Number.EPSILON) * 1000) / 1000)
      if (
        Math.round((videoElement.current.currentTime + Number.EPSILON) * 1000) /
          1000 <
          currentStartTime / 1000 ||
        Math.round((videoElement.current.currentTime + Number.EPSILON) * 1000) /
          1000 +
          0.05 >
          currentEndTime / 1000
      ) {
        setVideoPlayerAtLine(currentlySelectedLine)
      }
    } else if (
      videoElement.current.currentTime >= videoElement.current.duration
    ) {
      videoElement.current.currentTime = 0
      videoElement.current.play()
    }
  }

  function getCurrentSelectedLineStartTime() {
    if (currentlySelectedLine === null || currentlySelectedLine === undefined) {
      return null
    }
    return (
      currentVideoOffset +
      currentlySelectedLine.t_start +
      (currentlySelectedLine.start_adj !== undefined
        ? currentlySelectedLine.start_adj
        : 0)
    )
  }

  function getCurrentSelectedLineEndTime() {
    if (currentlySelectedLine === null || currentlySelectedLine === undefined) {
      return null
    }
    return (
      currentVideoOffset +
      currentlySelectedLine.t_end +
      (currentlySelectedLine.end_adj !== undefined
        ? currentlySelectedLine.end_adj
        : 0)
    )
  }

  function handleSetCurrentVideoPlayerToLineAction() {
    if (isTimingLocked) return
    const newVideoOffset = Math.floor(videoElement.current.currentTime * 1000)

    if (currentlySelectedLine === null) {
      setSongData({ ...songData, lyrics_adj: newVideoOffset })
      setCurrentlyHighlightedLine(null)
    } else {
      const indexOfSelectedLine = lyrics.indexOf(currentlySelectedLine)
      const newLineOffset =
        newVideoOffset -
        lyrics[indexOfSelectedLine].t_start -
        currentVideoOffset -
        (lyrics[indexOfSelectedLine].start_adj !== undefined
          ? lyrics[indexOfSelectedLine].start_adj
          : 0)
      var newLyrics = [...lyrics]
      newLyrics.splice(0, indexOfSelectedLine)
      for (var i = 0; i < newLyrics.length; i++) {
        let line = newLyrics[i]
        handleIncrementDecrementButton(line, true, false, newLineOffset, false)
        handleIncrementDecrementButton(line, false, false, newLineOffset, false)
      }
      setVideoPlayerAtLine(newLyrics[0])
    }
  }

  function handleIncrementDecrementToLineAndFollowingLines(
    line,
    isStart,
    isDecrement,
    value,
    setPlayer
  ) {
    if (isTimingLocked) return
    if (currentlySelectedLine !== null) {
      const indexOfSelectedLine = lyrics.indexOf(currentlySelectedLine)
      var newLyrics = [...lyrics]
      newLyrics.splice(0, indexOfSelectedLine)
      for (var i = 0; i < newLyrics.length; i++) {
        let line = newLyrics[i]
        if (line.line !== '') {
          handleIncrementDecrementButton(
            line,
            isStart,
            isDecrement,
            value,
            setPlayer
          )
        }
      }
      setVideoPlayerAtLine(newLyrics[0])
    }
  }

  function handleIncrementDecrementButton(
    line,
    isStart,
    isDecrement,
    value,
    setPlayer
  ) {
    if (isTimingLocked) return
    const indexOfSelectedLine = lyrics.indexOf(line)
    const newLyrics = [...lyrics]

    if (
      newLyrics[indexOfSelectedLine].start_adj === undefined ||
      newLyrics[indexOfSelectedLine].start_adj === null
    ) {
      newLyrics[indexOfSelectedLine].start_adj = 0
    }
    if (
      newLyrics[indexOfSelectedLine].end_adj === undefined ||
      newLyrics[indexOfSelectedLine].end_adj === null
    ) {
      newLyrics[indexOfSelectedLine].end_adj = 0
    }

    if (isStart) {
      if (isDecrement) {
        newLyrics[indexOfSelectedLine].start_adj -= value //isShiftDown ? 1000 : 100
      } else {
        const videoDuration = videoElement.current.duration * 1000
        const newValue = newLyrics[indexOfSelectedLine].start_adj + value
        newValue + newLyrics[indexOfSelectedLine].t_start + currentVideoOffset >
        videoDuration
          ? (newLyrics[indexOfSelectedLine].start_adj =
              videoDuration -
              newLyrics[indexOfSelectedLine].t_start -
              currentVideoOffset)
          : (newLyrics[indexOfSelectedLine].start_adj = newValue)
      }
      if (setPlayer) {
        setVideoPlayerAtLine(newLyrics[indexOfSelectedLine])
      }
    } else {
      if (isDecrement) {
        newLyrics[indexOfSelectedLine].end_adj -= value //isShiftDown ? 1000 : 100
      } else {
        const videoDuration = videoElement.current.duration * 1000
        const newValue = newLyrics[indexOfSelectedLine].end_adj + value
        newValue + newLyrics[indexOfSelectedLine].t_end + currentVideoOffset >
        videoDuration
          ? (newLyrics[indexOfSelectedLine].end_adj =
              videoDuration -
              newLyrics[indexOfSelectedLine].t_end -
              currentVideoOffset)
          : (newLyrics[indexOfSelectedLine].end_adj = newValue)
      }
      if (setPlayer) {
        setVideoPlayerAtEndOfALine(newLyrics[indexOfSelectedLine])
      }
    }
    setLyrics(newLyrics)
  }

  function handleStartEndInputValueChange(line, value, isStartTime) {
    if (isTimingLocked) return
    const indexOfSelectedLine = lyrics.indexOf(line)
    const newLyrics = [...lyrics]

    if (
      newLyrics[indexOfSelectedLine].start_adj === undefined ||
      newLyrics[indexOfSelectedLine].start_adj === null
    ) {
      newLyrics[indexOfSelectedLine].start_adj = 0
    }
    if (
      newLyrics[indexOfSelectedLine].end_adj === undefined ||
      newLyrics[indexOfSelectedLine].end_adj === null
    ) {
      newLyrics[indexOfSelectedLine].end_adj = 0
    }

    if (isStartTime) {
      const adj =
        value -
        newLyrics[indexOfSelectedLine].t_start -
        newLyrics[indexOfSelectedLine].start_adj -
        currentVideoOffset
      // console.log(adj)
      newLyrics[indexOfSelectedLine].start_adj += adj
    } else {
      const adj =
        value -
        newLyrics[indexOfSelectedLine].t_end -
        newLyrics[indexOfSelectedLine].end_adj -
        currentVideoOffset
      newLyrics[indexOfSelectedLine].end_adj += adj
    }
    setLyrics(newLyrics)
  }
  return (
    <Wrapper>
      <TeasePopup closePopup={handlePopupClose} isOpen={isOpen}>
        <CreateTeasePopupContent
          closePopup={handlePopupClose}
          artist={artist}
          songData={songData}
          setSongData={setSongData}
        />
      </TeasePopup>
      {!teaseIsLoading && songData && (
        <Header>
          <Navigation breadcrumbs={renderBreadcrumbs(songData)} />
          <HeaderInfo className={classes.HeaderInfo}>
            <ArtistAndSongName
              song={songData}
              badge={
                <PublicationBadge size="md" status={'tease'} text={'Tease'} />
              }
            />
            {songData?.video?.video?.url ? (
              <Actions>
                {isTeaseUpdating ? (
                  <Loader isLoading={isTeaseUpdating} />
                ) : (
                  <>
                    <div style={{ position: 'relative' }}>
                      <DisabledOverlay show={feelsSyncStatus === 'pending'} />
                      <CheckboxButton
                        onClick={onSyncVerifiedClick}
                        condition={songData.is_sync_verified}
                        label={'Sync Verified'}
                        disabled={feelsSyncStatus === 'pending'}
                      />
                    </div>
                    <div style={{ position: 'relative' }}>
                      <DisabledOverlay show={feelsSyncStatus === 'pending'} />
                      <ButtonOutline
                        color={'pink'}
                        size="small"
                        text={'EDIT CONTENT'}
                        onClick={onEditClick}
                        disabled={feelsSyncStatus === 'pending'}
                      />
                    </div>
                    <div style={{ position: 'relative' }}>
                      <DisabledOverlay show={feelsSyncStatus === 'pending'} />
                      <ButtonOutline
                        disable={!isTeaseChanged || feelsSyncStatus === 'pending'}
                        color={'pink'}
                        size="small"
                        text={'SAVE'}
                        onClick={onSaveClick}
                        disabled={feelsSyncStatus === 'pending'}
                      />
                    </div>
                    <div style={{ position: 'relative' }}>
                      <DisabledOverlay show={feelsSyncStatus === 'pending'} />
                      <ButtonOutline
                        color={'pink'}
                        size="small"
                        text={'DONE'}
                        onClick={onDoneClick}
                        disabled={feelsSyncStatus === 'pending'}
                      />
                    </div>
                  </>
                )}
              </Actions>
            ) : null}
          </HeaderInfo>
        </Header>
      )}

      {!teaseIsLoading && songData && songData?.video?.video?.url ? (
        <section className={classes.content}>
          <div className={classes.content__data}>
            <div className={classes.actions}>
              <TextFields
                tease={songData}
                onChange={onTextFieldValueChange}
                setIsEditing={setIsEditing}
                handleOptionSelect={handleOptionSelect}
              />

              {hasDeviceAccess ? (
                <OffsetActions
                  onVideoOffsetValueChange={onVideoOffsetValueChange}
                  currentlySelectedLine={currentlySelectedLine}
                  onLoopLinesClick={onLoopLinesClick}
                  isLoopingLines={isLoopingLines}
                  tease={songData}
                  handleSetCurrentVideoPlayerToLineAction={
                    handleSetCurrentVideoPlayerToLineAction
                  }
                  feelsSyncStatus={feelsSyncStatus}
                  onSyncStatusChange={setFeelsSyncStatus}
                  onSyncComplete={handleFeelsSyncComplete}
                  isTimingLocked={isTimingLocked}
                />
              ) : null}
            </div>
            {hasDeviceAccess ? (
              <div className={classes.lyrics}>
                <h3 className={classes.lyrics__title}>Lyrics</h3>
                <div className={classes.lyrics__content}>
                  <EditLyrics
                    onLyricSelected={line => {
                      handleLyricSelected(line)
                    }}
                    videOffset={currentVideoOffset}
                    lyrics={lyrics}
                    currentlySelectedLine={currentlySelectedLine}
                    currentlyHighlightedLine={currentlyHighlightedLine}
                    isShiftDown={isShiftDown}
                    isAltDown={isAltDown}
                    isXDown={isXDown}
                    isEnabled={true}
                    canEditTiming={!isTimingLocked}
                    handleIncrementDecrementButton={(
                      line,
                      isStart,
                      isDecerement,
                      isShiftDown,
                      isAltDown,
                      isXDown
                    ) => {
                      handleIncrementDecrementButton(
                        line,
                        isStart,
                        isDecerement,
                        isShiftDown
                          ? 1000
                          : isAltDown
                          ? 20
                          : isXDown
                          ? 300
                          : 100,
                        true
                      )
                    }}
                    handleStartEndInputValueChange={(
                      line,
                      value,
                      isStartTime
                    ) => {
                      handleStartEndInputValueChange(line, value, isStartTime)
                    }}
                    showOverlay={feelsSyncStatus === 'pending'}
                    overlayMessage="FeelsSync in progress..."
                  />
                </div>
              </div>
            ) : (
              <DevicesRestrictionsNotification />
            )}
          </div>

          {hasDeviceAccess ? (
            <div className={classes.content__video}>
              <TeaseVideo
                videoElement={videoElement}
                videoUrl={songData.video.video.url}
              />
            </div>
          ) : null}
        </section>
      ) : (
        <>
          {!teaseIsLoading && !teaseData && (
            <h1 style={{ color: 'white' }}>
              Something went wrong. Please try reloading the page.
            </h1>
          )}
          {(teaseIsLoading ||
            isTeaseFetching ||
            !songData?.video?.video?.url) && (
            <Loader
              isLoading={
                teaseIsLoading || isTeaseFetching || songData?.video?.video?.url
              }
            />
          )}
        </>
      )}
    </Wrapper>
  )
}
